import { environment } from '@environments/environment';
import { ShareComparisonChartResp, ShareComparisonParam } from '@data-visualization/share-comparison-chart/Types';
import axios, { CancelTokenSource } from 'axios';
import {
  generateFinalizedParamsPOST,
  generateParamPA,
  generateParamsPOST,
  getComparisonPeriodType,
  paramsSerializer,
} from './utils/OverviewServiceUtils';
import { AveragePriceChartResp, AverageSellingParam } from '@data-visualization/average-selling-price-chart/Types';
import { BubbleChartParam, BubbleChartResp } from '@models/OpportunityReview';
import {
  ExportPerformanceBySegmentResp,
  PerformanceAnalysisParam,
} from '@data-visualization/performance-analysis-line-chart/Types';
import { ProductDataType } from '@pages/product-analyzer/Product';
import { isBaseTimePeriodFormat } from '@utils/PeriodUtils';
import { getCollectionsLiveDataParams } from '@utils/collectionUtils/CollectionUtils';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { getQueryClient } from '@queries/QueryClientWrapper';

const constructRetailerOverviewCombinedChartParams = (param: ShareComparisonParam) => {
  const { periodType, isUsingCollectionLiveData = false } = param;

  const effectivePeriodType = periodType;

  const queryParamsRollingKpi = generateParamsPOST({
    clientId: param.clientId,
    productGroup: param.productGroup,
    countries: param.countries,
    period: param.period,
    comparisonPeriod:
      typeof param.comparisonPeriod === 'object' && param.comparisonPeriod !== null
        ? JSON.stringify(param.comparisonPeriod)
        : String(param.comparisonPeriod),
    periodType: param.periodType,
    saleType: param.saleType,
    appliedFilters: param.appliedFilters,
    isSummary: param.isSummary,
  });

  const collectionsParam = getCollectionsLiveDataParams(
    isUsingCollectionLiveData,
    effectivePeriodType,
    param.comparisonPeriod as RelativePeriod,
  );

  const params = generateFinalizedParamsPOST(
    queryParamsRollingKpi,
    param.productType,
    param.productGroup,
    queryParamsRollingKpi.country,
  );
  return {
    ...params,
    ...collectionsParam,
  };
};

export const getShareComparisonChart = async (param: ShareComparisonParam): Promise<any> => {
  const qc = getQueryClient('retailer-overview');

  try {
    return await qc.ensureQueryData({
      queryKey: ['retailer-overview-rolling-kpi', param],
      queryFn: async (): Promise<ShareComparisonChartResp | null> => {
        const { data } = await axios.post<ShareComparisonChartResp>(
          `${environment.baseUrl}/v2/rolling-kpi`,
          constructRetailerOverviewCombinedChartParams(param),
          {
            params: {},
          },
        );

        return data.salesRecordsFound ? data : null;
      },
      retry: false,
    });
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err}`);
  }
};

const constructRetailerOverviewLineChartParams = (param: AverageSellingParam) => {
  const { periodType, isUsingCollectionLiveData = false } = param;

  const effectivePeriodType = periodType;

  const queryParams = generateParamsPOST({
    clientId: param.clientId,
    productGroup: param.productGroup,
    countries: param.countries,
    period: param.period,
    comparisonPeriod:
      typeof param.comparisonPeriod === 'object' && param.comparisonPeriod !== null
        ? JSON.stringify(param.comparisonPeriod)
        : String(param.comparisonPeriod),
    periodType: periodType,
    saleType: param.saleType,
    appliedFilters: param.appliedFilters,
    isSummary: param.isSummary,
  });

  const collectionsParam = getCollectionsLiveDataParams(
    isUsingCollectionLiveData,
    effectivePeriodType,
    param.comparisonPeriod as RelativePeriod,
  );

  const params = generateFinalizedParamsPOST(queryParams, param.productType, param.productGroup, queryParams.country);

  return {
    ...params,
    ...collectionsParam,
  };
};

export const getAverageSellingPriceChart = async (param: AverageSellingParam): Promise<any> => {
  const qc = getQueryClient('retailer-overview');
  try {
    return await qc.ensureQueryData({
      queryKey: ['retailer-overview-average-selling-price', param],
      queryFn: async (): Promise<AveragePriceChartResp | null> => {
        const { data } = await axios.post<AveragePriceChartResp>(
          `${environment.baseUrl}/v2/rolling-kpi/average-selling-price`,
          constructRetailerOverviewLineChartParams(param),
          {
            params: {},
          },
        );
        return data.salesRecordsFound ? data : null;
      },
      retry: false,
    });
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err}`);
  }
};

const constructOpportunityReviewBubbleChartParams = (param: BubbleChartParam) => {
  const {
    periodType,
    clientId,
    productGroup,
    country,
    period,
    comparisonPeriod,
    saleType,
    appliedFilters,
    isSummary,
    isUsingCollectionLiveData = false,
  } = param;

  const effectivePeriodType = periodType;

  const queryParams = generateParamsPOST({
    clientId: clientId,
    productGroup: productGroup,
    countries: country,
    period: period,
    comparisonPeriod: comparisonPeriodToLabel(comparisonPeriod),
    periodType: effectivePeriodType,
    saleType: saleType,
    appliedFilters: appliedFilters,
    isSummary: isSummary,
  });

  const collectionsParam = getCollectionsLiveDataParams(
    isUsingCollectionLiveData,
    effectivePeriodType,
    comparisonPeriod as RelativePeriod,
  );

  return {
    ...queryParams,
    ...collectionsParam,
    productType: param.productType,
    productDataType: ProductDataType.PerformanceAnalyzer,
    productGroup: param.productGroup?.map((pg) => pg.value),
    country: queryParams.country,
    comparisonPeriodType: getComparisonPeriodType(comparisonPeriodToString(param.comparisonPeriod)),
    showPerformance: param.segmentName,
    dataType: param.segmentDataType,
  };
};

export const getOpportunityReviewBubbleChart = async (param: BubbleChartParam): Promise<BubbleChartResp | null> => {
  const qc = getQueryClient('opportunity-review');
  const params = constructOpportunityReviewBubbleChartParams(param);

  const queryKey = ['opportunity-review-bubble-chart', { ...param, params }] as const;

  const url = '/v2/opportunity-review/swot-chart';

  return await qc.fetchQuery<BubbleChartResp | null>({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        const response = await axios.post<BubbleChartResp>(`${environment.baseUrl}${url}`, params, {
          params: {},
          signal,
        });
        const data = response?.data;
        return data?.salesRecordsFound ? data : new BubbleChartResp();
      } catch (err: any) {
        console.error(`An error has occurred: ${err?.message ?? err}`);
        if (err?.response?.status == 403) {
          return null;
        }
        throw err;
      }
    },
  });
};

export const getExportOpportunityReviewBubbleChart = async (
  param: BubbleChartParam,
): Promise<ExportPerformanceBySegmentResp> => {
  const qc = getQueryClient('opportunity-review');
  const params = constructOpportunityReviewBubbleChartParams(param);

  const queryKey = ['opportunity-review-export-bubble-chart', { ...param, params }] as const;

  const url = '/v2/opportunity-review/export-swot-chart';

  return qc.fetchQuery<ExportPerformanceBySegmentResp>({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        const response = await axios.post<ExportPerformanceBySegmentResp>(`${environment.baseUrl}${url}`, params, {
          params: {},
          signal,
        });
        const data = response?.data;
        return data?.salesRecordsFound ? data : new ExportPerformanceBySegmentResp();
      } catch (err) {
        console.error('An error has occurred:', err);
        throw err;
      }
    },
  });
};

let cancelTokenSource: CancelTokenSource;

export const getExportPerformanceAnalysisLineChart = async (
  param: PerformanceAnalysisParam,
): Promise<ExportPerformanceBySegmentResp> => {
  try {
    const queryParamsExport = generateParamPA(param);

    const { data } = await axios.post<ExportPerformanceBySegmentResp>(
      `${environment.baseUrl}/v2/performance-analysis/export-performance-by-segment`,
      queryParamsExport,
      {
        paramsSerializer: paramsSerializer,
        cancelToken: cancelTokenSource?.token,
      },
    );
    return data;
  } catch (err: any) {
    console.error(`An error has occurred: ${err}`);
    throw new Error(err);
  }
};

const comparisonPeriodToLabel = (comparisonPeriod: RelativePeriod | BaseTimePeriod) => {
  if (
    comparisonPeriod === RelativePeriod.PreviousYearCurrentPeriod ||
    comparisonPeriod === RelativePeriod.PreviousPeriod
  ) {
    return comparisonPeriod;
  }

  return comparisonPeriod.label;
};

export const comparisonPeriodToString = (comparisonPeriod) => {
  return isBaseTimePeriodFormat(comparisonPeriod) ? RelativePeriod.PreviousPeriod : comparisonPeriod;
};
